import {XMarkIcon} from '@heroicons/react/24/outline'
import React, {FormEvent, useEffect, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import {DUPLICATE_SPONSOR_NAME_ERROR_IDENTIFIER} from '../../errors/errorIdentifiers'
import ServerError from '../../errors/server'
import {SponsorType} from '../../hooks/useSponsors'
import Dialog from '../Dialog'
import FlatButton from '../FlatButton'
import TextInput from '../TextInput/AdminTextInput'
import Toast from '../Toast'
import InlineImageUpload from './components/InlineImageUpload'

type Props = Readonly<{
  sponsorType: SponsorType
  open: boolean
  error?: Error | null
  onClose: () => void
  onConfirm: (name: string, type: SponsorType, image?: File) => void
  isBusy?: boolean
}>

function AddSponsorDialog({
  sponsorType,
  open,
  error,
  onClose,
  onConfirm,
  isBusy = false
}: Props) {
  const intl = useIntl()
  const formId = 'add-sponsor'
  const [state, setState] = useState<{
    name: string
    nameError: string | null
    image: File | null
  }>({
    name: '',
    nameError: null,
    image: null
  })

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setState({
        name: '',
        nameError: null,
        image: null
      })
    }
  }, [open])

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()

    // Validate name
    if (!state.name.trim()) {
      setState(prev => ({
        ...prev,
        nameError: intl.formatMessage({id: 'admin.manageSponsors.required'})
      }))
    } else {
      setState(prev => ({...prev, nameError: null}))
      // Note: image is optional, can be null
      onConfirm(state.name.trim(), sponsorType, state.image ?? undefined)
    }
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <div className="flex flex-col items-center">
        <div className="mb-6 mt-6 flex w-full items-center justify-between">
          <Dialog.Title className="typography-headline-4">
            <FormattedMessage
              id="admin.manageSponsors.add_sponsor_with_type"
              values={{
                type: intl.formatMessage({
                  id:
                    sponsorType === 'NATIONAL'
                      ? 'admin.manageSponsors.national'
                      : 'admin.manageSponsors.community'
                })
              }}
            />
          </Dialog.Title>
          <button className="-mr-2 -mt-2" type="button" onClick={onClose}>
            <span className="sr-only">
              <FormattedMessage id="general.Close" />
            </span>
            <XMarkIcon aria-hidden className="size-7" />
          </button>
        </div>

        {error &&
          !(
            ServerError.isServerError(error) &&
            ServerError.includes(error, DUPLICATE_SPONSOR_NAME_ERROR_IDENTIFIER)
          ) && (
            <div className="mb-4 w-full">
              <Toast variant="error">
                <FormattedMessage id="general.GenericError" />
              </Toast>
            </div>
          )}

        <form id={formId} onSubmit={handleSubmit} className="w-full space-y-8">
          <div className="flex items-start justify-between p-2 text-navy">
            <div className="typography-headline-5 after:content-[':']">
              <FormattedMessage id="admin.manageSponsors.sponsor_image" />
            </div>
            <InlineImageUpload
              onImageSelect={image => {
                setState(prev => ({...prev, image}))
              }}
              onImageRemove={() => {
                setState(prev => ({...prev, image: null}))
              }}
              currentImageUrl={
                state.image ? URL.createObjectURL(state.image) : null
              }
            />
          </div>

          <TextInput
            id="sponsor-name"
            label={intl.formatMessage({id: 'admin.manageSponsors.name'})}
            value={state.name}
            onChange={e => setState(prev => ({...prev, name: e.target.value}))}
            errorMessage={
              state.nameError ||
              (error &&
              ServerError.isServerError(error) &&
              ServerError.includes(
                error,
                DUPLICATE_SPONSOR_NAME_ERROR_IDENTIFIER
              )
                ? intl.formatMessage({
                    id: 'admin.manageSponsors.duplicate_name'
                  })
                : null)
            }
          />
        </form>

        <div className="mt-8 flex justify-center gap-4">
          <FlatButton variant="success" onClick={onClose} width="auto">
            <FormattedMessage id="admin.manageSponsors.cancel" />
          </FlatButton>
          <FlatButton
            variant="success"
            type="submit"
            form={formId}
            width="auto"
            disabled={isBusy}
          >
            <FormattedMessage id="general.save" />
          </FlatButton>
        </div>
      </div>
    </Dialog>
  )
}

export default AddSponsorDialog
